import React, { ReactNode } from "react";
import Cookies from "js-cookie";

import {
  ErrorBoundary as ReactErrorBoundary,
  FallbackProps,
} from "react-error-boundary";
import ErrorPage from "./ErrorPage";

declare global {
  interface Window {
    rollbar?: {
      error: (error: Error) => void;
    };
  }
}

function isChunkError(error: Error | undefined): boolean {
  if (!error) return false;
  return (
    error.message?.includes("Failed to fetch dynamically imported module") ||
    error.message?.includes("error loading dynamically imported module") ||
    error.name === "ChunkLoadError"
  );
}

function ErrorBoundary({ children }: { children: ReactNode }) {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    if (isChunkError(error)) {
      console.error(
        "Dynamic import error detected. This usually happens after a deployment.",
        {
          error,
          message: error.message,
          stack: error.stack,
        }
      );
      window.location.reload(); // For the modals or other lazy loaded components that will lose references when a release is deployed
      return;
    }

    console.error(error, errorInfo);
  };

  const FallbackComponent: React.FC<FallbackProps> = ({ error }) => {
    if (isChunkError(error)) return null;

    const isAuthenticated = Boolean(Cookies.get("_s"));
    if (!isAuthenticated) return null;
    return <ErrorPage />;
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={FallbackComponent}
      onError={handleError}
    >
      {children}
    </ReactErrorBoundary>
  );
}

export default ErrorBoundary;
